import type { Action } from "@/components/Action.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WProgressTicker, type WProgressTickerProps } from "@/components/WProgressBar.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { notifyError } from "@/modules/error/errorReporting.ts";
import { showWarn } from "@/modules/error/errorStream.ts";
import { getEnglishText, type Translatable } from "@/modules/l10n/getTransText.ts";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { cn } from "@/utils/classNames.ts";
import { isStorybook } from "@/utils/environment.ts";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { cssTransition, Slide, toast, type ToastOptions } from "react-toastify";

export type ToastId = number | string;

/** Properties accessible later from the notification center */
export interface ToastifyData {
    icon?: string;
    /* Do not show a notification in the notification center (only toast) */
    noNotification?: boolean;
}

export type ToastType = "success" | "error" | "info" | "warning" | "progress";

/** Represents a toast notification. */
export interface Toast extends ToastifyData {
    /** The message content of the toast. Can be an arbitrary React component. */
    message: ReactNode;

    /** The action button of the toast. */
    button?: ReactNode;

    /** Unique identifier. */
    toastId?: ToastId;

    /** CSS class name for the icon */
    icon?: string;
    /** The severity level of the toast. */
    type: ToastType;

    /** The duration in milliseconds for the toast to automatically close. Set to `false` to disable auto close. */
    autoClose?: number | false;

    /** The progress value of the toast. A number between 0 and 1. */
    progress?: number;
    hideProgressBar?: boolean;
}

function renderToastMessage({ message, button }: Toast) {
    return (
        <div className="flex justify-between items-center content-center gap-0.25em">
            {message}
            {button}
        </div>
    );
}

const DisabledTransition = cssTransition({
    enter: "noop",
    exit: "noop",
    collapse: false,
});

/** Convert {@link Toast} properties into {@link ToastOptions} for Toastify. */
function normalizeToast({
    type,
    toastId,
    autoClose,
    progress,
    hideProgressBar = true,
    icon,
    ...rest
}: Toast): ToastOptions<ToastifyData> {
    // Disable auto-close for tests to capture all of the toasts in snapshots
    if (isStorybook) {
        autoClose ??= false;
    }

    return {
        toastId,
        type: type === "progress" ? "success" : type,
        className: type === "progress" ? "Toastify__toast--progress" : undefined,

        autoClose,
        progress,
        // NB: Small hack to make toasts animate from the top (to match notification button position)
        transition: (props) =>
            // Disable the transition in Storybook for stable snapshots
            isStorybook ? <DisabledTransition {...props} /> : <Slide {...props} position="top-center" />,
        hideProgressBar,
        icon: icon ? <MaskIcon className={cn(icon, "size-1rem")} /> : undefined,

        data: {
            // Store the icon for the notification center
            icon,
            ...rest,
        },
    };
}

export function raiseToast(options: Toast): ToastId {
    return toast(renderToastMessage(options), normalizeToast(options));
}

/** @public */
export function updateToast(id: ToastId, options: Toast) {
    toast.update(id, normalizeToast(options));
}

export function dismissToast(id: ToastId) {
    if (!toast.isActive(id)) {
        showWarn("Tried to dismiss inactive toast id=%o", id);
    }
    // NB: Work around react-toastify bug where `dismiss` will not remove notifications from `useNotificationCenter()`
    toast.update(id, { data: { noNotification: true } });
    toast.dismiss(id);
}

export function clearAllToasts() {
    toast.dismiss();
}

//#region Toast error utilities
const toastedErrors = new WeakSet<Error>();

/** Raise a toast while checking for duplicates (of the same error being handled) */
function raiseErrorToast(err: unknown, toast: Toast) {
    if (err instanceof Error) {
        if (toastedErrors.has(err)) {
            console.log("Double error toast suppressed: %s", err);
            return undefined;
        } else {
            toastedErrors.add(err);
        }
    }
    return raiseToast(toast);
}

function errorToToast(err: unknown, prefix: ReactNode): Toast {
    return {
        message: (
            <ClipBoardTooltip isHtml={true}>
                {prefix ?? <Trans>Error</Trans>}: {errorToReactMessage(err)}
            </ClipBoardTooltip>
        ),
        type: "error",
        icon: "jp-wrong-icon",
    };
}

//#endregion

//#region Request status toasts
export interface RequestToastOptions extends Omit<Toast, "message" | "type" | "buttonLabel" | "buttonLink"> {
    /** Message to show on success. */
    success: ReactNode;
    successType?: ToastType;
    /** "View" button link to display on success. */
    viewLink?: Action;

    /** Message to show on error. */
    error: ReactNode;
    /** "Retry" button link to display on error. */
    retryLink?: Action;

    /** Whether to suppress/hide the toast on success. */
    silent?: boolean;
}

function makeRequestToast(
    err: Error | null,
    {
        success,
        error,
        successType = "info",
        viewLink,
        retryLink,
        noNotification,
        ...options
    }: Omit<RequestToastOptions, "silent">,
): Toast {
    const messageOptions: Toast = err
        ? {
              ...errorToToast(err, error),
              autoClose: retryLink ? false : undefined,
              button: !!retryLink && (
                  <WButton color="primary" variant="ghost" size="bar" action={retryLink}>
                      <Trans>Retry</Trans>
                  </WButton>
              ),
          }
        : {
              message: success,
              type: successType,
              button: !!viewLink && (
                  <WButton color="primary" variant="ghost" size="bar" action={viewLink}>
                      <Trans>View</Trans>
                  </WButton>
              ),
              // NB: Only success toasts respect `noNotification`, errors will always show in the notification center
              noNotification,
          };
    return {
        ...messageOptions,
        ...options,
    };
}

export function raiseRequestToast(err: Error | null, { silent, ...options }: RequestToastOptions): ToastId | undefined {
    if (silent && !err) {
        return undefined;
    }

    const toast = makeRequestToast(err, options);
    if (err) {
        raiseErrorToast(err, toast);
    } else {
        raiseToast(toast);
    }
}

//#endregion

//#region Progress toasts
export interface ProgressToastOptions extends Omit<Toast, "message" | "type"> {
    label?: WProgressTickerProps["label"];
    lifeTime: WProgressTickerProps["lifeTime"];
}

export function makeProgressToast({ label, lifeTime, ...options }: ProgressToastOptions): Toast {
    return {
        message: <WProgressTicker label={label} startTime={performance.now()} lifeTime={lifeTime} />,
        type: "progress",
        autoClose: lifeTime,
        ...options,
    };
}

export function raiseProgressToast(options: ProgressToastOptions): ToastId | undefined {
    return raiseToast(makeProgressToast(options));
}

//#endregion

//#region Error toasts

/** Show a toast for an error that can not be displayed inline (eg. in a form) */
export function raiseBackgroundErrorToast(err: unknown, prefix: Translatable, options?: Partial<Toast>) {
    const prefixEng = getEnglishText(prefix);
    console.error("Background error: %s:", prefixEng, err);
    notifyError(err, { context: { message: prefixEng } });
    return raiseErrorToast(err, {
        ...errorToToast(err, prefix),
        ...options,
    });
}

export function raiseSavingChangesErrorToast(err: unknown) {
    return raiseBackgroundErrorToast(err, <Trans>Error saving changes</Trans>);
}

//#endregion
